import _ from "lodash";
import { v4 as uuidv4 } from "uuid";

function generate(wpd, wpdDocument, dataEntryVM) {
	return (function (vm) {
		let currentAxis = null;

		/**
		 * User script entrypoint.
		 */
		function run() {
			attachListeners();
		}

		/**
		 * Attaches listeners to WPD events.
		 */
		function attachListeners() {
			// unregister all previous listeners
			wpd.events.removeAllListeners("wpd.axes.add");
			wpd.events.removeAllListeners("wpd.axes.select");
			wpd.events.removeAllListeners("wpd.axes.delete");
			wpd.events.removeAllListeners("wpd.dataset.add");
			wpd.events.removeAllListeners("wpd.dataset.select");
			wpd.events.removeAllListeners("wpd.dataset.point.add");
			wpd.events.removeAllListeners("wpd.dataset.point.select");
			wpd.events.removeAllListeners("wpd.dataset.point.delete");

			// axis select
			wpd.events.addListener("wpd.axes.select", (payload) => {
				const axis = payload.axes;
				const tweakButton = wpdDocument.getElementById(
					"tweak-axes-calibration-button",
				);
				const tableTweakButton = wpdDocument.getElementById(
					"tweak-table-axis-calibration-button",
				);

				const axisType = wpd.custom.identifyAxis(axis);
				if (axisType === "table") {
					// hide default tweak button and show custom tweak button
					tweakButton.classList.add("hidden");
					tableTweakButton.classList.remove("hidden");

					const axis = wpd.tree.getActiveAxes();
					const calibration = axis.getMetadata()?.calibration;
					const wizardStep = calibration?.wizardStep;
					const calibrationComplete = calibration?.calibrationComplete;
					if (!calibrationComplete) {
						if (wizardStep === "defineTableArea") {
							wpd.alignAxes.pickArea(true);
						} else if (!wizardStep || wizardStep === "defineRowsAndColumns") {
							wpd.alignAxes.backToRowsAndColumns(calibration);
						} else if (wizardStep === "definePrefillSettings") {
							wpd.graphicsWidget.setTool(
								new wpd.TableHeaderTool(calibration, true),
							);
							wpd.alignAxes.displayDetailsSidebar(axis.getMetadata());
						}
					} else {
						wpd.alignAxes.displayDetailsSidebar(axis.getMetadata());
					}
				} else if (axisType === "xy") {
					const metadata = axis.getMetadata();
					const wizardStep = metadata?.wizardStep;
					const calibrationComplete = metadata?.calibrationComplete;
					if (dataEntryVM.reworkXYGraphCalibration && !calibrationComplete) {
						if (wizardStep === "defineAxisPoints") {
							wpd.xyGraphEdit.startAxisPointsCalibration(false);
						} else if (wizardStep === "defineAxisValues") {
							wpd.xyGraphEdit.startDefineAxisValues(false);
						} else if (wizardStep === "defineAxisDataTypes") {
							wpd.xyGraphEdit.startDefineAxisDataTypes(false);
						} else if (wizardStep === "defineAxisStudyBranches") {
							wpd.xyGraphEdit.startDefineAxisStudyBranches(false);
						} else {
							wpd.xyGraphEdit.startAxisPointsCalibration(false);
						}
					} else {
						wpd.sidebar.show("edit-xy-graph-sidebar");
						wpd.graphicsWidget.removeRepainter();
						wpd.graphicsWidget.setRepainter(
							new wpd.AlignmentCornersRepainter(axis.calibration),
						);
						wpd.graphicsWidget.forceHandlerRepaint();
						wpd.graphicsWidget.setTool(new wpd.XYGraphPointTool(axis));
					}
				} else if (axisType === "bar") {
					wpd.sidebar.show("edit-bar-graph-sidebar");
					wpd.graphicsWidget.removeTool();
					wpd.graphicsWidget.resetData();
					wpd.graphicsWidget.setTool(
						new wpd.BarHeadersTool(axis, dataEntryVM, true),
					);
				} else {
					// non-table axes should have the default tweak button displayed
					tweakButton.classList.remove("hidden");
					tableTweakButton.classList.add("hidden");

					// unset default image point tools
					wpd.graphicsWidget.removeRepainter();
					wpd.graphicsWidget.removeTool();
					wpd.graphicsWidget.resetData();
					wpd.sidebar.clear();
				}
			});

			// axis delete
			wpd.events.addListener("wpd.axes.delete", () => {
				wpd.graphicsWidget.removeTool();
				wpd.graphicsWidget.removeRepainter();
				wpd.graphicsWidget.resetData();
			});

			// dataset add
			wpd.events.addListener("wpd.dataset.add", (payload) => {
				const dataset = payload.dataset;

				// associate dataset with the active axis
				wpd.appData
					.getPlotData()
					.setAxesForDataset(dataset, wpd.tree.getActiveAxes());

				// include alpha value for data points
				dataset.colorRGB = new wpd.Color(
					...wpd.Colors.getValues(wpd.Colors.goodA),
				);

				// this block is not useful anymore
				if (!dataEntryVM.pointGroupOverhaul) {
					// overwrite point group save function if dataset is a table dataset
					if (dataset.getMetadata()?.table) {
						dataset.setPointGroups = function (pointGroups) {
							this._groupNames = pointGroups;

							// get metadata tags from dactyl
							const map = {};
							const tags = vm.getTags();
							pointGroups.forEach((uuid) => {
								const tag = tags.find((tag) => tag.uuid === uuid);

								map[tag.uuid] = tag.name;
							});

							const metadata = dataset.getMetadata();
							dataset.setMetadata(_.assign(metadata, { tagMap: map }));
						};
					}
				}
			});

			// dataset select
			wpd.events.addListener("wpd.dataset.select", (payload) => {
				const plotData = wpd.appData.getPlotData();

				// store current axis for metadata dialog
				currentAxis = plotData.getAxesForDataset(payload.dataset);

				// set table header tool if this is a table column
				if (
					currentAxis instanceof wpd.ImageAxes &&
					currentAxis.getMetadata().table &&
					payload.dataset.getMetadata().table
				) {
					// unset default image point tools
					wpd.graphicsWidget.removeRepainter();
					wpd.graphicsWidget.removeTool();
					wpd.graphicsWidget.resetData();
					wpd.sidebar.clear();

					// set table header tool
					wpd.graphicsWidget.setTool(
						new wpd.TableCellSelectionTool(
							currentAxis,
							payload.dataset.getMetadata().colIndex,
						),
					);
				} else if (wpd.custom.isGraphAxis(currentAxis)) {
					// make sure tag group controls are hidden
					wpd.tagGroups.hideControls();

					const metadata = payload.dataset.getMetadata();
					const parsedKey = wpd.CompositeKeyUtils.parseCompositeKey(
						metadata.studyData.compositeKey,
					);
					wpd.tagGroups.setActiveKeys(parsedKey);
					wpd.tagGroups.refreshStudies(metadata.studyData.compositeKey, true);

					if (dataEntryVM.readOnly) {
						setTimeout(() => wpd.acquireData.adjustPoints(), 0);
					} else {
						wpd.acquireData.manualSelection();
					}
				}

				// set focus on canvas
				wpdDocument.getElementById("topCanvas").dispatchEvent(
					new MouseEvent("mousedown", {
						bubbles: true,
						button: 0,
						buttons: 1,
						composed: true,
						detail: 1,
					}),
				);
			});

			// data point add
			wpd.events.addListener("wpd.dataset.point.add", async (payload) => {
				const dataPoint = payload.dataset.getPixel(payload.index);
				if (wpd.custom.isGraphAxis(currentAxis)) {
					const [
						studyKeyTag,
						partKeyTag,
						armKeyTag,
						populationKeyTag,
						subgroupKeyTag,
					] = wpd.CompositeKeyUtils.branchTags.map((tag) =>
						_.find(wpd.tagGroups.tags, { name: tag }),
					);

					// do not trigger metadata dialog for graphs
					const axisType = wpd.custom.identifyAxis(currentAxis);
					const studyData = payload.dataset.getMetadata().studyData;
					const axisMetadata = currentAxis.getMetadata();
					const axesLabelData = axisMetadata.axesLabelData;

					if (studyData && axesLabelData) {
						// graph data adds
						// add data points for study arm abbreviation, x-value,
						// x-unit, y-value, y-unit
						const valueUnitTag = wpd.tagGroups.findTag({
							name: "Unit",
						});
						const timeUnitTag = wpd.tagGroups.findTag({
							name: "Time (unit)",
						});
						const descriptionTag = wpd.tagGroups.findTag({
							name: "Description",
						});

						const [xValue, yValue] = currentAxis.pixelToData(
							dataPoint.x,
							dataPoint.y,
						);

						// find the "change from baseline" tag
						const changeFromBaselineTag = wpd.tagGroups.findTag({
							name: "Change from baseline",
						});

						// define the tag group shared across the set of data points
						const tagGroup = {
							name: studyData.tagGroup.name,
							uuid: studyData.tagGroup.uuid,
							instanceUuid: uuidv4(),
						};

						if (axisType === "xy") {
							// handle xy graphs axis data
							// find the right value tag for x and y values
							let xValueTag = wpd.tagGroups.findTag({
								name: axesLabelData.x.dataType,
							});

							let yValueTag = wpd.tagGroups.findTag({
								name: axesLabelData.y.dataType,
							});

							// replace x or y value tag with the active point group tag
							const activePointGroup = wpd.tree.getActivePointGroup();

							if (activePointGroup) {
								switch (activePointGroup.axisDirection) {
									case "x":
										xValueTag = activePointGroup.tag;
										break;
									case "y":
										yValueTag = activePointGroup.tag;
										break;
								}
							}

							// find the right unit tag for x and y units
							const xUnitTag =
								axesLabelData.x.dataType === "Time (number)"
									? timeUnitTag
									: valueUnitTag;

							const yUnitTag =
								axesLabelData.y.dataType === "Time (number)"
									? timeUnitTag
									: valueUnitTag;

							let metadata;
							let mIndex = 0;

							const pointGroupAxisDirectionKey = dataEntryVM.pointGroupOverhaul
								? "pointGroupAxisDirection"
								: "tagGroupAxisDirection";

							// add study arm abbreviation, x-value, x-unit, y-value,
							// y-unit to the newly added data point
							metadata = {
								type: axisType,
								tagGroup: tagGroup,
								...(payload.dataset.hasPointGroups()
									? {
											[pointGroupAxisDirectionKey]:
												activePointGroup?.axisDirection ?? "center",
									  }
									: {}),
								compositeKey: wpd.CompositeKeyUtils.generateCompositeKey(
									wpd.tagGroups.activeStudyKey,
									wpd.tagGroups.activePartKey,
									wpd.tagGroups.activeArmKey,
									wpd.tagGroups.activePopulationKey,
									wpd.tagGroups.activeSubgroupKey,
								),
								uuid: uuidv4(),
							};

							const keyMappings = [
								{
									activeKey: "activeStudyKey",
									tag: studyKeyTag,
								},
								{
									activeKey: "activePartKey",
									tag: partKeyTag,
								},
								{
									activeKey: "activeArmKey",
									tag: armKeyTag,
								},
								{
									activeKey: "activePopulationKey",
									tag: populationKeyTag,
								},
								{
									activeKey: "activeSubgroupKey",
									tag: subgroupKeyTag,
								},
							];

							keyMappings.forEach(({ activeKey, tag }) => {
								const val = wpd.tagGroups[activeKey];
								if (val) {
									setMetadata(metadata, tag, val, mIndex++, true);
								}
							});

							metadata[mIndex++] = {
								tag: xValueTag,
								value: wpd.utils.roundAxisValue(
									xValue,
									currentAxis.isLogX(),
									axesLabelData.x.decimals,
								),
								axis: "x",
								axisType: "value",
							};
							metadata[mIndex++] = {
								tag: xUnitTag,
								value: axesLabelData.x.unit,
								axis: "x",
								axisType: "unit",
							};

							if (axesLabelData.x.description) {
								metadata[mIndex++] = {
									tag: descriptionTag,
									value: axesLabelData.x.description,
									axis: "x",
									axisType: "description",
								};
							}

							metadata[mIndex++] = {
								tag: yValueTag,
								value: wpd.utils.roundAxisValue(
									yValue,
									currentAxis.isLogY(),
									axesLabelData.y.decimals,
								),
								axis: "y",
								axisType: "value",
							};
							metadata[mIndex++] = {
								tag: yUnitTag,
								value: axesLabelData.y.unit,
								axis: "y",
								axisType: "unit",
							};

							if (axesLabelData.y.description) {
								metadata[mIndex++] = {
									tag: descriptionTag,
									value: axesLabelData.y.description,
									axis: "y",
									axisType: "description",
								};
							}

							if (axesLabelData.x.changeFromBaseline) {
								// add change from baseline data
								metadata[mIndex++] = {
									tag: changeFromBaselineTag,
									value: axesLabelData.x.changeFromBaseline,
									axis: "x",
									axisType: "changeFromBaseline",
								};
							}
							if (axesLabelData.y.changeFromBaseline) {
								metadata[mIndex++] = {
									tag: changeFromBaselineTag,
									value: axesLabelData.y.changeFromBaseline,
									axis: "y",
									axisType: "changeFromBaseline",
								};
							}

							mIndex = pushBroadcastTags(
								metadata,
								mIndex,
								axesLabelData,
								payload.dataset.getMetadata()?.uuid,
							);

							if (
								dataEntryVM.pointGroupOverhaul &&
								payload.dataset.hasPointGroups()
							) {
								metadata.tuple = wpd.pointGroups.generateTupleData(
									payload.dataset,
									{
										metadata,
										x: dataPoint.x,
										y: dataPoint.y,
									},
									activePointGroup?.axisDirection || "center",
									activePointGroup?.tag?.uuid,
								);

								// prevent adding a center point with a duplicate tuple ID
								if (
									dataEntryVM.pointGroupProximityFixes &&
									!activePointGroup &&
									wpd.pointGroups.hasTupleIdCollision(
										payload.dataset,
										metadata.tuple,
										payload.dataset.getPixel(payload.index),
									)
								) {
									payload.dataset.removePixelAtIndex(payload.index);
									wpd.dataPointCounter.setCount(payload.dataset.getCount());
									dataEntryVM.showNotification({
										message:
											"Cannot add central tendency point: another central tendency with the same data values already exists. Move the point to a distinct position.",
										status: "warning",
									});
									return;
								}
							}

							payload.dataset.setMetadataAt(payload.index, metadata);

							// update point group data
							if (
								!dataEntryVM.pointGroupOverhaul &&
								payload.dataset.hasPointGroups()
							) {
								// refresh dataset tuples
								wpd.pointGroups.refreshTuples(payload.dataset);
							}
						} else if (axisType === "bar") {
							if (dataEntryVM.updateDataPointsOnBarHeaderEdit) {
								// handle bar graph axis data
								const barHeaders = currentAxis.getMetadata().barHeaders;

								if (barHeaders) {
									// bar axis pixelToData always returns one value
									const value = xValue;

									// figure out if categories are on the vertical
									// or horizontal axis
									const currentRotation = wpd.graphicsWidget.getRotation();
									const swapAxes =
										currentRotation && currentRotation % 180 !== 0;

									const initialRotation =
										currentAxis.getMetadata().initialRotation;
									const rotationDifference = Math.abs(
										currentRotation - initialRotation,
									);
									const swapXY =
										rotationDifference > 0 && rotationDifference % 180 !== 0;
									let [valueOrientation, headerOrientation] =
										barHeaders.headerOrientation === "HORIZONTAL"
											? swapAxes
												? ["x", "y"]
												: ["y", "x"]
											: swapAxes
											? ["y", "x"]
											: ["x", "y"];

									if (swapXY) {
										headerOrientation = headerOrientation === "x" ? "y" : "x";
										valueOrientation = valueOrientation === "x" ? "y" : "x";
									}

									// find the right value tag for value
									let valueTag = wpd.tagGroups.findTag({
										name: axesLabelData.bar.dataType,
									});

									// replace x or y value tag with the active point group tag
									const activePointGroup = wpd.tree.getActivePointGroup();

									if (activePointGroup) {
										valueTag = activePointGroup.tag;
									}

									// find the right unit tag for units
									const unitTag =
										axesLabelData.bar.dataType === "Time (number)"
											? timeUnitTag
											: valueUnitTag;

									// add length of bar header data to lastIndex
									const headerData = wpd.barGraphEdit.getHeaderDataForDataPoint(
										currentAxis,
										dataPoint,
									);

									let lastIndex = 2 + (headerData?.length || 0);

									// add change from baseline data
									const changeFromBaseline = {};
									if (axesLabelData.bar.changeFromBaseline) {
										changeFromBaseline[++lastIndex] = {
											tag: changeFromBaselineTag,
											value: axesLabelData.bar.changeFromBaseline,
											axis: valueOrientation,
										};
									}

									// add study arm abbreviation, x-value, x-unit, y-value,
									// y-unit to the newly added data point
									let mIndex = 0;
									const metadata = {
										...changeFromBaseline,
										type: axisType,
										tagGroup: tagGroup,
										...(payload.dataset.hasPointGroups() &&
										dataEntryVM.pointGroupOverhaul
											? {
													pointGroupAxisDirection:
														activePointGroup?.axisDirection ?? "center",
											  }
											: {}),
										compositeKey: studyData.compositeKey,
										uuid: uuidv4(),
									};

									const parsedKey = wpd.CompositeKeyUtils.parseCompositeKey(
										studyData.compositeKey,
									);

									if (headerData) {
										headerData.forEach((header) => {
											if (
												header.tag.name ===
												wpd.CompositeKeyUtils.referenceBranchTag
											) {
												metadata.referenceCompositeKey = header.value;
											}
											metadata[mIndex++] = {
												...header,
												axis: headerOrientation,
												// The categorical (header) Description is typed
												// "description" so it propagates to every bar
												// symmetric with the value-axis description
												// (DCTL-1833 / DHELP-204); every other header
												// tag stays a plain categorical "value".
												axisType:
													header.tag?.uuid === descriptionTag.uuid
														? "description"
														: "value",
											};
										});
									}

									const keyMappings = [
										{ key: "study", tag: studyKeyTag },
										{ key: "studyPart", tag: partKeyTag },
										{ key: "studyArm", tag: armKeyTag },
										{
											key: "population",
											tag: populationKeyTag,
										},
										{
											key: "subgroup",
											tag: subgroupKeyTag,
										},
									];

									keyMappings.forEach(({ key, tag }) => {
										const val = parsedKey[key];
										if (val) {
											setMetadata(metadata, tag, val, mIndex++, true);
										}
									});

									metadata[mIndex++] = {
										tag: valueTag,
										value: wpd.utils.roundAxisValue(
											value,
											currentAxis.isLog(),
											axesLabelData.bar.decimals,
										),
										axis: valueOrientation,
										axisType: "value",
									};

									metadata[mIndex++] = {
										tag: unitTag,
										value: axesLabelData.bar.unit,
										axis: valueOrientation,
										axisType: "unit",
									};

									if (axesLabelData.bar.description) {
										metadata[mIndex++] = {
											tag: descriptionTag,
											value: axesLabelData.bar.description,
											axis: valueOrientation,
											axisType: "description",
										};
									}
									if (axesLabelData.bar.changeFromBaseline) {
										metadata[mIndex++] = {
											tag: changeFromBaselineTag,
											value: axesLabelData.bar.changeFromBaseline,
											axis: valueOrientation,
											axisType: "changeFromBaseline",
										};
									}

									mIndex = pushBroadcastTags(
										metadata,
										mIndex,
										axesLabelData,
										payload.dataset.getMetadata()?.uuid,
									);

									if (
										dataEntryVM.pointGroupOverhaul &&
										payload.dataset.hasPointGroups()
									) {
										metadata.tuple = wpd.pointGroups.generateTupleData(
											payload.dataset,
											{
												metadata,
												x: dataPoint.x,
												y: dataPoint.y,
											},
											activePointGroup?.axisDirection || "center",
											activePointGroup?.tag?.uuid,
										);
									}

									payload.dataset.setMetadataAt(payload.index, metadata);

									// update point group data
									if (
										!dataEntryVM.pointGroupOverhaul &&
										payload.dataset.hasPointGroups()
									) {
										// refresh dataset tuples
										wpd.pointGroups.refreshTuples(payload.dataset);
									}
								}
							} else {
								// handle bar graph axis data
								const barHeaders = currentAxis.getMetadata().barHeaders;

								if (barHeaders) {
									const headers = barHeaders.headers;

									// bar axis pixelToData always returns one value
									const value = xValue;

									// figure out if categories are on the vertical
									// or horizontal axis
									const [valueOrientation, headerOrientation] =
										barHeaders.headerOrientation === "HORIZONTAL"
											? ["y", "x"]
											: ["x", "y"];

									const startKey = `start${headerOrientation.toUpperCase()}`;
									const endKey = `end${headerOrientation.toUpperCase()}`;
									const headerIndex = _.findIndex(headers, (header) => {
										return _.inRange(
											dataPoint[headerOrientation],
											header[startKey],
											header[endKey],
										);
									});

									// prepare header data for insertion
									let lastIndex = 2;
									const headerData =
										headerIndex > -1
											? _.chain(headers[headerIndex].metadata)
													.omitBy(wpd.utils.isWord)
													.keys()
													.reduce((result, key) => {
														result[++lastIndex] =
															headers[headerIndex].metadata[key];

														return result;
													}, {})
													.value()
											: null;

									// find the right value tag for value
									let valueTag = wpd.tagGroups.findTag({
										name: axesLabelData.bar.dataType,
									});

									// replace x or y value tag with the active point group tag
									const activePointGroup = wpd.tree.getActivePointGroup();

									if (activePointGroup) {
										valueTag = activePointGroup.tag;
									}

									// find the right unit tag for units
									const unitTag =
										axesLabelData.bar.dataType === "Time (number)"
											? timeUnitTag
											: valueUnitTag;

									// add change from baseline data
									const changeFromBaseline = {};
									if (axesLabelData.bar.changeFromBaseline) {
										changeFromBaseline[++lastIndex] = {
											tag: changeFromBaselineTag,
											value: axesLabelData.bar.changeFromBaseline,
											axis: valueOrientation,
										};
									}

									// add study arm abbreviation, x-value, x-unit, y-value,
									// y-unit to the newly added data point
									let mIndex = 0;
									const metadata = {
										...changeFromBaseline,
										type: axisType,
										tagGroup: tagGroup,
										compositeKey: studyData.compositeKey,
										uuid: uuidv4(),
									};

									const parsedKey = wpd.CompositeKeyUtils.parseCompositeKey(
										studyData.compositeKey,
									);

									if (headerData) {
										Object.values(headerData).forEach((header) => {
											if (
												header.tag.name ===
												wpd.CompositeKeyUtils.referenceBranchTag
											) {
												metadata.referenceCompositeKey = header.value;
											}
											metadata[mIndex++] = header;
										});
									}

									const keyMappings = [
										{ key: "study", tag: studyKeyTag },
										{ key: "studyPart", tag: partKeyTag },
										{ key: "studyArm", tag: armKeyTag },
										{
											key: "population",
											tag: populationKeyTag,
										},
										{
											key: "subgroup",
											tag: subgroupKeyTag,
										},
									];

									keyMappings.forEach(({ key, tag }) => {
										const val = parsedKey[key];
										if (val) {
											setMetadata(metadata, tag, val, mIndex++, true);
										}
									});

									metadata[mIndex++] = {
										tag: valueTag,
										value: wpd.utils.roundAxisValue(
											value,
											currentAxis.isLog(),
											axesLabelData.bar.decimals,
										),
										axis: valueOrientation,
										axisType: "value",
									};

									metadata[mIndex++] = {
										tag: unitTag,
										value: axesLabelData.bar.unit,
										axis: valueOrientation,
										axisType: "unit",
									};

									if (axesLabelData.bar.description) {
										metadata[mIndex++] = {
											tag: descriptionTag,
											value: axesLabelData.bar.description,
											axis: valueOrientation,
											axisType: "description",
										};
									}

									mIndex = pushBroadcastTags(
										metadata,
										mIndex,
										axesLabelData,
										payload.dataset.getMetadata()?.uuid,
									);

									payload.dataset.setMetadataAt(payload.index, metadata);

									// update point group data
									if (payload.dataset.hasPointGroups()) {
										// refresh dataset tuples
										wpd.pointGroups.refreshTuples(payload.dataset);
									}
								}
							}
						}
					}
				} else {
					await wpd.custom.processDataPoint(
						payload.dataset,
						payload.index,
						currentAxis,
					);

					// switch to adjust data point tool
					wpd.acquireData.adjustPoints();
				}

				// select all the added data points
				payload.dataset.unselectAll();

				// repaint the dataset and refresh the zoom image
				wpd.graphicsWidget.forceHandlerRepaint();
				const { x, y } = dataPoint;
				wpd.graphicsWidget.updateZoomToImagePosn(x, y);
			});

			// data point select
			wpd.events.addListener("wpd.dataset.point.select", async (payload) => {
				wpd.tagGroups.isEditing = true;
				if (payload.indexes.length && payload.indexes[0] >= 0) {
					// at least one point has been selected and this is not
					// an unselect (unselected returns [-1])

					// redraw points
					wpd.graphicsWidget.forceHandlerRepaint();

					if (!payload.positionOnly && payload.indexes.length === 1) {
						await wpd.custom.processDataPoint(
							payload.dataset,
							payload.indexes[0],
							currentAxis,
							payload.isBranchEdit,
						);
					} else {
						// multiple points selected
						// TODO: Disabling metadata collection for multiple points
						// for now; we need to figure out what happens if you select
						// points from different tag groups
						// await wpd.custom.processDataPoints(
						// 	payload.dataset,
						// 	payload.indexes,
						// 	payload.isBranchEdit
						// );
					}
				}

				wpd.tagGroups.isEditing = false;
			});

			// data point delete
			wpd.events.addListener("wpd.dataset.point.delete", (payload) => {
				// refresh tag group displays
				if (wpd.tagGroups.activeTagGroup) {
					wpd.tagGroups.refreshAll(true);
				}

				wpd.custom.syncMetadataKeys(payload.dataset);
			});
		}

		wpd.events.addListener("wpd.dataset.point.hover", async (payload) => {
			if (payload.index >= 0) {
				await wpd.custom.openMetadataPreview(payload.dataset, payload.index);
			} else if (
				dataEntryVM.viewEditOverlappingDataPoints &&
				payload.indexes &&
				payload.indexes.length > 0
			) {
				await wpd.custom.openMetadataPreviewV2(
					payload.dataset,
					payload.indexes,
				);
			}
		});

		wpd.events.addListener("wpd.dataset.point.closeHover", async () => {
			await wpd.custom.closeMetadataPreview();
		});

		function setMetadata(metadata, tag, value, index, hidden) {
			if (value) {
				metadata[index] = {
					tag: {
						name: tag.name,
						uuid: tag.uuid,
					},
					value: value,
					...(hidden ? { hidden: true } : {}),
				};
			}
		}

		// Broadcast tags are graph-level tags applied to every extracted point.
		// Only non-empty values are stamped at extract time - empty-value broadcast
		// rows are injected into the per-point edit dialog later (via processDataPoint
		// in custom-functions) so the curator sees a pre-populated empty slot but
		// the metadata isn't polluted with empty rows that filterCapturedMetadata
		// would strip on the next save anyway.
		// Entries scoped to a specific datasetUuid only apply when datasetUuid
		// matches the dataset the point belongs to; entries with no datasetUuid
		// apply graph-wide. When a tag has both graph-wide and branch-scoped
		// entries, branch-scoped wins.
		function pushBroadcastTags(metadata, mIndex, axesLabelData, datasetUuid) {
			const broadcastTags = axesLabelData?.broadcastTags;
			if (!Array.isArray(broadcastTags) || broadcastTags.length === 0) {
				return mIndex;
			}
			// All broadcast rows accumulate independently. Multiple rows of
			// the same tag -- whether graph-wide, branch-scoped, or mixed --
			// all land on each point.
			for (const broadcast of broadcastTags) {
				if (!broadcast?.tagName) continue;
				if (!broadcast.value) continue;
				if (broadcast.datasetUuid && broadcast.datasetUuid !== datasetUuid) {
					continue;
				}
				const tag = wpd.tagGroups.findTag({ name: broadcast.tagName });
				if (!tag) continue;
				metadata[mIndex++] = {
					tag: { name: tag.name, uuid: tag.uuid },
					value: broadcast.value,
					fromBroadcast: true,
					broadcastValue: broadcast.value,
					...(broadcast.datasetUuid
						? { datasetUuid: broadcast.datasetUuid }
						: {}),
				};
			}
			return mIndex;
		}

		return {
			run: run,
		};
	})(dataEntryVM);
}

export { generate };
